import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { faLaugh } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SendIcon from '@mui/icons-material/Send';
import Avatar from '@mui/material/Avatar';
import { useGridMode } from 'contexts/GridModeContext';
import TableSettingsContext from 'contexts/TableSettingsContext';
import TexasChatContext from 'contexts/TexasChatContext';
import EmojiPicker from 'emoji-picker-react';
import { useAppSelector } from 'hooks';
import { useKeyboardOffset } from 'hooks/useKeyboardOffset';
import {
  ACTION_BAR_SELECTOR,
  CHAT_TOGGLE,
  panelToggleProps,
  useOutsideTapDismiss
} from 'hooks/useOutsideTapDismiss';
import { useTableScopedOpen } from 'hooks/useTableScopedOpen';
import { useTableSelector } from 'hooks/useTableSelector';
import { chatMessageKey } from 'reducers/currentTable';
import type { ReadThrough } from 'reducers/liveChatNotifications';
import useGameService from 'services/GameService';
import type { RootState } from 'store';
import { swipeIgnoreProps } from 'utils/swipeIgnore';
import { APP_DOMAIN } from 'utils/urls';

import { getCommsPanelStyle } from 'components/CommsPanel/panelGeometry';
import { TablePanelBoundary, useTablePanelDismiss } from 'components/TablePanel/TablePanelBoundary';

import styles from './Chat.module.scss';

const NO_AUTO_SCROLL_AREA_PERCENTAGE = 0.95;

const UNMUTE_MESSAGE_HIDE_DELAY = 15000; //15 seconds

// Match the server's `c1..c20` rolling-buffer convention — the panel never
// displays more than the 20 most recent chat entries.
const CHAT_DISPLAY_LIMIT = 20;

// Geometry (position, edges, width, z-index, visibility) is shared with Hand
// History - the two panels are siblings that take turns in one slot - see
// components/CommsPanel/panelGeometry.ts.

/**
 * Resolve a chat-message avatar value to a usable <img src>. The server sends just the
 * filename (matching the rest of the app's avatar handling — see TableHeader / ProfilePanel
 * which build the URL as `https://media.${APP_DOMAIN}/images/${avatar}`). If the field is
 * already a full URL or a data URI, return it unchanged. Returns undefined for missing
 * avatars so MUI Avatar falls back to its initials/icon.
 */
const resolveAvatarSrc = (avatar: any): string | undefined => {
  if (!avatar || typeof avatar !== 'string') return undefined;
  if (/^(https?:|data:|blob:)/i.test(avatar)) return avatar;
  return `https://media.${APP_DOMAIN}/images/${avatar}`;
};

/**
 * A chat message timestamp in ms, or undefined when it isn't one. Server
 * payloads sometimes carry `ts` as seconds (matches `seatStartTime`) and
 * sometimes as ms; auto-detect by magnitude — anything below 10^12 is seconds.
 * Exported for unit testing.
 */
export const chatTsToMs = (ts: any): number | undefined => {
  const n = Number(ts);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return n < 1e12 ? n * 1000 : n;
};

/**
 * Format a chat message timestamp into "MMM D, h:mm:ss AM/PM" (e.g. "Apr 28, 2:36:05 PM").
 */
const formatChatTime = (ts: any): string => {
  const ms = chatTsToMs(ts);
  if (ms === undefined) return '';
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
};

/** Name for the emoji button, so tapping it never double-toggles the picker. */
const EMOJI_TOGGLE = 'chat-emoji';

const isRenderableChatMessage = (chatMessage: any): boolean =>
  !!chatMessage && !!chatMessage.message;

const Chat = () => {
  const { isOpen, setIsOpen } = useContext(TexasChatContext);
  const { chatEnabled } = useContext(TableSettingsContext);
  // Paint it only on the table it belongs to, and unmount it when closed so a
  // failed or empty panel can never leave a blurred overlay over the table.
  const open = useTableScopedOpen(!!isOpen) && chatEnabled;
  const close = useCallback(() => setIsOpen(false), [setIsOpen]);
  useTablePanelDismiss(open, close);
  if (!open) return null;
  return (
    <TablePanelBoundary onClose={close}>
      <ChatContent onClose={close} />
    </TablePanelBoundary>
  );
};

const ChatContent = ({ onClose }: { onClose: () => void }) => {
  const { isOpen, setIsOpen } = useContext(TexasChatContext);
  const panelRef = useRef<HTMLDivElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  // Tapping the felt outside the panel closes it, exactly as the header X does.
  // Only mounted while open on this table, so the hook is always enabled.
  useOutsideTapDismiss({
    enabled: true,
    surfaceRef: panelRef,
    onDismiss: onClose,
    toggleName: CHAT_TOGGLE,
    swallowTapWithin: ACTION_BAR_SELECTOR
  });
  const { chatEnabled } = useContext(TableSettingsContext);
  const isGridMode = useGridMode();
  const { textChat, clearUnreadMessage } = useGameService();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatMessagesRef = useRef<HTMLInputElement>(null);
  const game = useTableSelector((table) => table.game);
  const players = useTableSelector((table) => table.players);
  const seatId = useTableSelector((table) => table.currentPlayer.seatId);
  const playerIsMuted = +players[+seatId]?.isMuted;
  const playerMuteDuration = players[+seatId]?.duration;
  const { chatMessages, lastChatMessage, gameID, game: gameTable } = game;
  const currentPlayerGUID = useAppSelector((state) => state.user.playerGUID);
  const currentPlayerAvatar = useAppSelector((state) => state.user.playerAvatar);
  const [isShowEmojiPicker, setIsShowEmojiPicker] = useState(false);
  // The picker is its own dismissable surface INSIDE the chat panel: a tap in
  // the message list closes just the picker and leaves chat up, while a tap out
  // on the felt closes both (the chat hook above owns that half).
  useOutsideTapDismiss({
    enabled: isShowEmojiPicker,
    surfaceRef: emojiPickerRef,
    onDismiss: () => setIsShowEmojiPicker(false),
    toggleName: EMOJI_TOGGLE
  });
  const currentPlayer = useTableSelector((table) => table.currentPlayer);
  const { isSeated } = currentPlayer;
  const prevChatMessagesLengthRef = useRef(chatMessages.length);
  const chatMessagesContainerRef = useRef<HTMLDivElement>(null);

  const mutedPlayers =
    players?.filter((f: any) => +f?.isMuted && f.GUID !== currentPlayerGUID) ?? [];

  const playerIsMutedPrev = useRef<number>();
  const prevIsOpenRef = useRef<boolean>(false);

  const [isUnmuted, setIsUnmuted] = useState(false);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (!playerIsMuted && playerIsMutedPrev.current) {
      playerIsMutedPrev.current = playerIsMuted;

      setIsUnmuted(true);

      timeoutId = setTimeout(() => {
        setIsUnmuted(false);
      }, UNMUTE_MESSAGE_HIDE_DELAY);
    }

    playerIsMutedPrev.current = playerIsMuted;

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [playerIsMuted]);

  const sendMessage = () => {
    if ((chatMessagesRef.current?.value ?? '').length > 0) {
      textChat(chatMessagesRef.current?.value ?? '');
    }
    (chatMessagesRef.current as any).value = '';

    messagesEndRef.current?.scrollIntoView({
      behavior: prevChatMessagesLengthRef.current !== chatMessages.length ? 'smooth' : 'auto',
      block: 'center'
    });
  };

  const handleKeyDown = (event: any) => {
    const keyCode = event.which || event.keyCode;

    if (keyCode === 13 && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const addEmoji = (e: any) => {
    const sym = e.unified.split('-');
    const codesArray: any = [];
    sym.forEach((el: any) => codesArray.push('0x' + el));
    const emoji = String.fromCodePoint(...codesArray);
    if (chatMessagesRef.current) {
      (chatMessagesRef.current as any).value += emoji;
    }
    setIsShowEmojiPicker(false);
  };

  // Intentionally NOT auto-focusing the input on open — focusing it pops the
  // on-screen keyboard immediately, which we don't want. The keyboard should
  // only appear once the player taps the message field themselves.

  // First-time open: snap unconditionally to the latest message. The other
  // scroll effect below only nudges when the user was already near the
  // bottom — on first open scrollTop is 0 (top), so without this hook the
  // user lands on the OLDEST message and has to scroll down themselves.
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      // Wait one tick so the panel is laid out before measuring.
      const t = window.setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
      }, 50);
      prevIsOpenRef.current = true;
      return () => window.clearTimeout(t);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen]);

  // Lift the chat above the on-screen keyboard (keyboard overlays the webview;
  // see useKeyboardOffset / capacitor.config Keyboard.resize = 'none').
  const keyboardOffset = useKeyboardOffset();

  // What the panel displays (rendered below), so the unread clear only ever
  // counts a message as read if the player can actually see it:
  // - Drop empty / malformed entries before slicing so the 20-cap counts real
  //   messages, not nulls.
  // - Table chat is shared history, not a per-seat session: show the full
  //   buffer whether the player is observing or seated. (We used to drop
  //   messages from before seatStartTime once seated, which wiped the visible
  //   history the moment a player joined the table.) Sending is still gated by
  //   isSeated below.
  // - Chat visibility is controlled by `chatEnabled` and nothing else. This
  //   list used to be filtered by `muteOtherPlayers` as well, but that setting
  //   is audio-only ("Mute Other Players' Sounds") — it must never hide a
  //   message.
  // - Match the server's `c1..c20` rolling-buffer convention by showing only
  //   the most recent 20 messages. The reducer keeps a larger buffer (200) to
  //   make dedup/merge stable across partial server pushes; we just don't
  //   render the older ones.
  const displayedMessages = chatMessages.filter(isRenderableChatMessage).slice(-CHAT_DISPLAY_LIMIT);
  const displayedMessagesKey = displayedMessages.map(chatMessageKey).join(',');

  // This table's unread entry while its count is positive, and the newest
  // message behind that count.
  const findUnreadHere = (state: RootState) =>
    state.liveChatNotifications.find(
      (n) => Number(n.gameID) === Number(gameID) && n.unreadMessageCount > 0
    );
  const hasUnreadHere = useAppSelector((state) => !!findUnreadHere(state));
  const notifiedMessageGUID = useAppSelector(
    (state) => findUnreadHere(state)?.lastUnreadMessageGUID
  );
  const isNotifiedMessageDisplayed =
    !!notifiedMessageGUID &&
    displayedMessages.some((m: any) => m.messageGUID === notifiedMessageGUID);

  // What a clear reports as read: the newest message on screen. The server
  // keeps it as the read watermark (EN-10782). Two messages in one ms are told
  // apart by GUID, so at a tie it names the notified message when that is on
  // screen — otherwise the read wouldn't cover it.
  const newestDisplayed = displayedMessages.reduce((newest: any, m: any) => {
    const ts = chatTsToMs(m.ts) ?? 0;
    const newestTs = chatTsToMs(newest?.ts) ?? 0;
    if (!newest || ts !== newestTs) return !newest || ts > newestTs ? m : newest;
    return notifiedMessageGUID && newest.messageGUID === notifiedMessageGUID ? newest : m;
  }, undefined);
  // Both or neither: the server rejects a ts without its GUID, and a message
  // missing either (a legacy entry) gets the clear older clients send.
  const newestDisplayedTs = chatTsToMs(newestDisplayed?.ts);
  const readThrough: ReadThrough | undefined =
    newestDisplayed?.messageGUID && newestDisplayedTs
      ? { readMessageGUID: newestDisplayed.messageGUID, readMessageTs: newestDisplayedTs }
      : undefined;

  // Opening the panel reads what it shows, so it clears an unread count whose
  // newest message is displayed. The count can arrive before its message (two
  // feeds), so one whose message hasn't rendered yet is left for the effect
  // below, which clears once it shows. A count without a GUID predates counts
  // carrying one, so its messages are already in history and it clears. With
  // no count there is nothing to clear, and the server clear deletes the whole
  // record — sent anyway it could erase an increment landing in flight.
  // `Chat` mounts this panel only while it is actually on screen — open on THIS
  // table (useTableScopedOpen) with chat enabled — so a panel left open on a
  // table the player swiped away from, or with chat disabled, is unmounted and
  // a message arriving then stays unread; swiping back or re-enabling remounts
  // it and clears. Independent of the DOM refs below: clearUnreadMessage
  // touches no DOM, and gating it on the refs left the first open a no-op.
  useEffect(() => {
    if (hasUnreadHere && (!notifiedMessageGUID || isNotifiedMessageDisplayed)) {
      clearUnreadMessage(gameTable, gameID, currentPlayerGUID, readThrough);
    }
  }, [gameID, currentPlayerGUID]);

  // While open, the count is one aggregate over messages that arrive on two
  // feeds: the count (got_liveChatNotification) usually before the message
  // (the table feed). So a later change clears only once the NEWEST notified
  // message is displayed — not when some other message shows, which would wipe
  // the count for one still in flight, and not on the count alone. Keyed on the
  // whole displayed set, since currentTable sorts by timestamp and a late batch
  // can land a message BEHIND the newest. Not on `lastChatMessage`, which is
  // seated-only. A count without a GUID (stored before counts carried one)
  // clears when any message shows, as before — but only a count actually held:
  // with none yet (the message often renders before its count arrives), the
  // server clear would delete the record and could erase the increment in
  // flight. The count's arrival re-runs this, and clears once its message shows.
  const prevDisplayedMessagesKeyRef = useRef(displayedMessagesKey);
  const isUnreadEffectMountRef = useRef(true);
  useEffect(() => {
    const isDisplayChanged = prevDisplayedMessagesKeyRef.current !== displayedMessagesKey;
    prevDisplayedMessagesKeyRef.current = displayedMessagesKey;
    if (isUnreadEffectMountRef.current) {
      isUnreadEffectMountRef.current = false;
      return;
    }
    const isGUIDlessCount = hasUnreadHere && !notifiedMessageGUID;
    if (isNotifiedMessageDisplayed || (isDisplayChanged && isGUIDlessCount)) {
      clearUnreadMessage(gameTable, gameID, currentPlayerGUID, readThrough);
    }
  }, [displayedMessagesKey, notifiedMessageGUID]);

  useEffect(() => {
    if (messagesEndRef?.current && chatMessagesContainerRef?.current && isOpen) {
      const { scrollHeight, clientHeight, scrollTop } = chatMessagesContainerRef.current;
      const autoScrollRange = (scrollHeight - clientHeight) * NO_AUTO_SCROLL_AREA_PERCENTAGE;

      if (scrollTop > autoScrollRange) {
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({
            behavior: prevChatMessagesLengthRef.current !== chatMessages.length ? 'smooth' : 'auto',
            block: 'center'
          });
        }, 250);
      }
    }

    prevChatMessagesLengthRef.current = chatMessages.length;
  }, [lastChatMessage?.messageGUID, isOpen, gameID, currentPlayerGUID]);

  // Chat is disabled in Table Settings — render nothing.
  if (!chatEnabled) return null;

  return (
    <div
      ref={panelRef}
      className={styles.chat}
      style={{
        ...getCommsPanelStyle(isGridMode, true, { keyboardOffset, side: 'right' }),
        // Only the bottom edge moves (the on-screen keyboard lifts it).
        transition: 'bottom 0.15s ease'
      }}>
      <div className={styles.header}>
        <span>Game Chat</span>
        <button aria-label='Close game chat' onClick={() => setIsOpen(false)} style={closeBtn}>
          <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>&times;</span>
        </button>
      </div>
      {/* Owns its own scroll: the table-swipe Observer must hand wheel/pointer
          gestures here back to the browser rather than preventDefault them.
          See utils/swipeIgnore. */}
      <div
        ref={chatMessagesContainerRef}
        className={styles.body}
        style={{ height: '100%' }}
        {...swipeIgnoreProps}>
        <div
          className={styles.chat_list}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
            padding: '0.75rem',
            minHeight: '100%',
            justifyContent: 'end'
          }}>
          {/* The same list the unread clear reads — see displayedMessages. */}
          {displayedMessages.map((chatMessage: any) => (
            <div
              className={styles.chat_item}
              key={chatMessageKey(chatMessage)}
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '0.5rem',
                alignItems: 'flex-end',
                justifyContent: chatMessage.GUID === currentPlayerGUID ? 'end' : 'start'
              }}>
              {chatMessage.GUID !== currentPlayerGUID && (
                <Avatar
                  sx={{ width: 28, height: 28 }}
                  src={resolveAvatarSrc(chatMessage.avatar)}
                  alt={chatMessage.displayName || ''}>
                  {(chatMessage.displayName || '?').charAt(0).toUpperCase()}
                </Avatar>
              )}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: chatMessage.GUID === currentPlayerGUID ? 'flex-end' : 'flex-start'
                }}>
                {chatMessage.GUID !== currentPlayerGUID && (
                  <div style={chatHeaderRowStyle}>
                    {/* Render the display name as the server sent it.
                          toTitleCase used to round-trip "Apple_A" → "Apple_a"
                          because it lowercases the whole string and only
                          re-caps the first char of each space-separated
                          word; underscores aren't word boundaries. */}
                    <span style={chatNameStyle}>{chatMessage.displayName || ''}</span>
                    {formatChatTime(chatMessage.ts) && (
                      <span style={timestampStyle}>{formatChatTime(chatMessage.ts)}</span>
                    )}
                  </div>
                )}
                {chatMessage.GUID === currentPlayerGUID && formatChatTime(chatMessage.ts) && (
                  <div style={{ ...chatHeaderRowStyle, justifyContent: 'flex-end' }}>
                    <span style={timestampStyle}>{formatChatTime(chatMessage.ts)}</span>
                  </div>
                )}
                <div
                  className={styles.chat_detail}
                  style={{
                    wordBreak: 'break-all',
                    flexGrow: 0.01,
                    marginLeft: 0
                  }}>
                  <p style={{ whiteSpace: 'pre-line', fontSize: '0.85rem' }}>
                    {chatMessage.message}
                  </p>
                </div>
              </div>
              {chatMessage.GUID === currentPlayerGUID && (
                <Avatar
                  sx={{ width: 28, height: 28 }}
                  src={
                    resolveAvatarSrc(chatMessage.avatar) || resolveAvatarSrc(currentPlayerAvatar)
                  }
                  alt={chatMessage.displayName || ''}>
                  {(chatMessage.displayName || '?').charAt(0).toUpperCase()}
                </Avatar>
              )}
            </div>
          ))}
        </div>
        <div ref={messagesEndRef} />
      </div>

      {isUnmuted && (
        <div style={noticeArea}>
          <div style={noticeBubble}>
            Messaging has been reactivated. Please adhere to community guidelines to avoid further
            action.
          </div>
        </div>
      )}

      {mutedPlayers.length > 0 && (
        <div style={noticeArea}>
          {mutedPlayers.map((p: any, i: number) => (
            <div key={i} style={noticeBubble}>
              {p.displayName} has been suspended from chat
            </div>
          ))}
        </div>
      )}

      {isSeated && (
        <div style={chatInputArea}>
          {+playerIsMuted === 1 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '0.5rem',
                color: 'rgba(255,255,255,0.5)',
                fontSize: '0.8rem'
              }}>
              {playerMuteDuration}
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
              <button
                onClick={() => setIsShowEmojiPicker((prev) => !prev)}
                style={emojiBtn}
                {...panelToggleProps(EMOJI_TOGGLE)}>
                <FontAwesomeIcon
                  icon={faLaugh}
                  style={{ color: isShowEmojiPicker ? '#E98C26' : 'rgba(255,255,255,0.5)' }}
                />
              </button>
              <textarea
                ref={chatMessagesRef as any}
                onKeyDown={handleKeyDown}
                placeholder='Message...'
                rows={1}
                style={chatTextarea}
              />
              <button onClick={sendMessage} style={sendBtn}>
                <SendIcon style={{ fontSize: '1rem' }} />
              </button>
            </div>
          )}
          {isShowEmojiPicker && (
            <div
              ref={emojiPickerRef}
              style={{ position: 'absolute', bottom: '100%', left: 0, marginBottom: '0.5rem' }}>
              <EmojiPicker onEmojiClick={addEmoji} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Chat;

const closeBtn: React.CSSProperties = {
  background: 'rgba(255, 255, 255, 0.06)',
  border: 'none',
  borderRadius: '8px',
  color: 'rgba(255, 255, 255, 0.5)',
  padding: '0.25rem 0.5rem',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginLeft: 'auto'
};

const chatInputArea: React.CSSProperties = {
  position: 'relative',
  padding: '0.6rem 0.75rem',
  borderTop: '1px solid rgba(255, 255, 255, 0.06)',
  backgroundColor: 'rgba(0, 0, 0, 0.2)'
};

const chatTextarea: React.CSSProperties = {
  flex: 1,
  background: 'rgba(255, 255, 255, 0.06)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '10px',
  color: '#fff',
  fontSize: '0.85rem',
  fontFamily: 'inherit',
  padding: '0.5rem 0.75rem',
  resize: 'none',
  outline: 'none',
  minHeight: '36px',
  maxHeight: '80px'
};

const emojiBtn: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: '0.4rem',
  fontSize: '1.1rem',
  display: 'flex',
  alignItems: 'center'
};

const noticeArea: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.4rem',
  padding: '0.5rem 0.75rem'
};

const noticeBubble: React.CSSProperties = {
  fontSize: '0.8rem',
  color: 'rgba(255, 255, 255, 0.7)',
  padding: '0.4rem 0.75rem',
  backgroundColor: 'rgba(255, 255, 255, 0.06)',
  borderRadius: '8px',
  lineHeight: 1.4
};

const chatHeaderRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: '6px',
  marginBottom: '2px'
};

const chatNameStyle: React.CSSProperties = {
  fontSize: '0.65rem',
  color: 'rgba(255, 255, 255, 0.55)',
  fontWeight: 600
};

const timestampStyle: React.CSSProperties = {
  fontSize: '0.6rem',
  color: 'rgba(255, 255, 255, 0.35)',
  letterSpacing: '0.02em',
  fontVariantNumeric: 'tabular-nums'
};

const sendBtn: React.CSSProperties = {
  background: 'linear-gradient(135deg, #E98C26, #d47a1a)',
  border: 'none',
  borderRadius: '10px',
  color: '#fff',
  padding: '0.5rem',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '36px',
  minHeight: '36px'
};
